import { useBuildActionContext } from "@/contexts/DetailActionContext";
import type { DetailActionContext as DetailActionContextValue } from "@/contexts/DetailActionContext/types";
import type { DetailPageConfig } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { BaseEntity, EntityHooks, SimpleTableConfig } from "../../../types";

interface ActionContextResult<TData extends BaseEntity> {
  actionContext: DetailActionContextValue<TData> | null;
}

/**
 * Builds action context for bulk actions from row actions
 * Provides context needed for bulk action execution
 */
export function useSimpleTableActionContext<TData extends BaseEntity>(
  selectedItemsArray: TData[],
  config: SimpleTableConfig<TData>,
  hooks: EntityHooks<TData>
): ActionContextResult<TData> {
  // `BuildActionContextParams.config` expects DetailPageConfig but the
  // SimpleTable carries the table config — build a minimal-valid stub instead
  // of casting through `unknown`.
  const detailPageConfig: DetailPageConfig<TData> = {
    entity: config.entity,
    displayName: config.entity,
    tabs: [],
    actions: [],
  };

  const actionContext = useBuildActionContext<TData>({
    item: selectedItemsArray[0] || null,
    hooks,
    refetch: () => Promise.resolve(),
    updateMutation: hooks.useUpdate(),
    deleteMutation: hooks.useDelete(),
    entityName: config.entity,
    config: detailPageConfig,
  });

  return {
    actionContext,
  };
}
